import { IReactiveModel, ViewModel } from '@descomplica/microfrontend-core';
import { IsOptional, IsString, Matches } from 'class-validator';
import { action, makeAutoObservable, observable } from 'mobx';
import { toast } from 'react-toastify';

import type {
  ApplicationDto,
  CreateOrganizationDto,
  OrganizationDto,
  UpdateOrganizationDto,
} from '@/shared/application/interfaces/pegasus-io.gateway';
import { ObservableForm } from '@/shared/presentation/forms/BaseForm';

import {
  CreateOrganizationCollaboration,
  ORGANIZATION_CREATED,
} from '../../application/collaborations/create-organization.collaboration';
import {
  APPLICATIONS_OBTAINED,
  GetApplicationsCollaboration,
} from '../../application/collaborations/get-applications.collaboration';
import {
  GetOrganizationsCollaboration,
  ORGANIZATIONS_OBTAINED,
} from '../../application/collaborations/get-organizations.collaboration';
import {
  ORGANIZATION_REMOVED,
  RemoveOrganizationCollaboration,
} from '../../application/collaborations/remove-organization.collaboration';
import {
  ORGANIZATION_UPDATED,
  UpdateOrganizationCollaboration,
} from '../../application/collaborations/update-organization.collaboration';

const isImageUrlRegex =
  /^(https?:\/\/)?([\da-z.-]+)\.([a-z.]{2,6})([/\w .-]*)*\/?\.(jpg|jpeg|png)(\?.*)?$/i;

export type ApplicationForm = {
  guid: string;
  name: string;
};

class Form {
  @IsOptional()
  guid: string;

  @IsString({ message: 'Campo obrigatório' })
  name: string;

  @Matches(isImageUrlRegex, { message: 'Deve ser a URL de uma imagem jpg ou png' })
  logo: string;

  @IsOptional()
  description: string;

  @IsOptional()
  applications: ApplicationForm[];
}

@ViewModel({ singleton: true })
export class OrganizationsViewModel implements IReactiveModel {
  @observable form = new ObservableForm(Form);
  @observable organizations: OrganizationDto[] = [];
  @observable applications: ApplicationDto[] = [];
  @observable isLoading = false;
  @observable hasError = false;
  @observable isSubmitting = false;
  @observable isRemoving = false;
  @observable isUpdating = false;
  @observable isCreateModalOpen = false;
  @observable isCreatedModalOpen = false;
  @observable isUpdateModalOpen = false;
  @observable organization: OrganizationDto;

  constructor(
    private getApplicationsCollaboration: GetApplicationsCollaboration,
    private getOrganizationsCollaboration: GetOrganizationsCollaboration,
    private createOrganizationCollaboration: CreateOrganizationCollaboration,
    private updateOrganizationCollaboration: UpdateOrganizationCollaboration,
    private removeOrganizationCollaboration: RemoveOrganizationCollaboration,
  ) {
    makeAutoObservable(this);
  }

  @action
  async onInit() {
    try {
      this.isLoading = true;
      const [org, app] = await Promise.all([
        this.getOrganizationsCollaboration.run(),
        this.getApplicationsCollaboration.run(),
      ]);

      if (org?.identifier !== ORGANIZATIONS_OBTAINED) {
        throw new Error('Erro ao obter organizações');
      }

      if (app?.identifier !== APPLICATIONS_OBTAINED) {
        throw new Error('Erro ao obter aplicações');
      }

      this.organizations = org.data;
      this.applications = app.data;
      this.isLoading = false;
    } catch (error) {
      console.error(error);
      this.hasError = true;
    }
  }

  @action
  async create() {
    try {
      this.isSubmitting = true;

      const dto: CreateOrganizationDto = {
        name: this.form.values.name,
        logo: this.form.values.logo,
        description: this.form.values.description,
      };

      const newOrg = await this.createOrganizationCollaboration.run(dto);
      if (newOrg?.identifier !== ORGANIZATION_CREATED) {
        throw new Error('Erro ao criar aplicação');
      }

      this.organizations = [...this.organizations, newOrg.data];

      this.form.reset();

      this.isCreateModalOpen = false;
      this.isCreatedModalOpen = true;
      this.organization = newOrg.data;
      toast.success('Organização criada com sucesso');
    } catch (error) {
      console.error(error);
      toast.error('Erro ao criar organização');
    } finally {
      this.isSubmitting = false;
    }
  }

  async openCreatedOrganization(organization: OrganizationDto) {
    window.open(`/admin/organizations/${organization.guid}`, '_blank');
    this.isCreatedModalOpen = false;
  }

  async closeCreatedModal() {
    this.isCreateModalOpen = false;
  }

  async update() {
    try {
      this.isUpdating = true;

      const dto: UpdateOrganizationDto = {
        guid: this.form.values.guid,
        name: this.form.values.name,
        logo: this.form.values.logo,
        description: this.form.values.description,
        applicationGuids: this.form.values.applications.map((app) => app.guid),
      };

      const org = await this.updateOrganizationCollaboration.run(dto);
      if (org?.identifier !== ORGANIZATION_UPDATED) {
        throw new Error('Erro ao atualizar organização');
      }

      this.organizations = [
        org.data,
        ...this.organizations.filter((organization) => organization.guid !== this.form.values.guid),
      ];

      this.form.reset();

      this.isUpdateModalOpen = false;

      toast.success('Organização atualizada com sucesso');
    } catch (error) {
      console.error(error);
      toast.error('Erro ao atualizar organização');
    } finally {
      this.isUpdating = false;
    }
  }

  async remove(guid: string) {
    try {
      this.isRemoving = true;

      const org = await this.removeOrganizationCollaboration.run(guid);
      if (org?.identifier !== ORGANIZATION_REMOVED) {
        throw new Error('erro ao delete organização');
      }

      this.organizations = this.organizations.filter((organization) => organization.guid !== guid);

      toast.success('Organização removida com sucesso');
    } catch (error) {
      console.error(error);
      toast.error('Erro ao remover organização');
    } finally {
      this.isRemoving = false;
    }
  }

  @action
  openCreateModal() {
    this.isCreateModalOpen = true;
  }

  @action
  closeCreateModal() {
    this.isCreateModalOpen = false;
  }

  @action
  openUpdateModal(organization: OrganizationDto) {
    this.form.setValue('guid', organization.guid);
    this.form.setValue('name', organization.name);
    this.form.setValue('logo', organization.logo);
    this.form.setValue('description', organization.description);
    this.form.setValue(
      'applications',
      organization.applications.map((app) => ({
        guid: app.guid,
        name: app.name,
      })),
    );

    this.isUpdateModalOpen = true;
  }

  @action
  closeUpdateModal() {
    this.form.reset();

    this.isUpdateModalOpen = false;
  }
}
